import { useState } from 'react';
import { BordButton, BordModal, BordOneToneIcon, BordSelect, BordTextButton } from 'bord-design-system';
import { QuoteService } from 'services/quote.service';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';

import './QuoteCancelModal.scss';

interface IQuoteCancelModal {
  quoteId: number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => Promise<void>;
}

/**
 * Modal de confirmación para cancelar una cotización con razón de cancelación.
 * Usado tanto en el detalle como en el editor de cotización.
 */
const QuoteCancelModal = ({ quoteId, open, onClose, onSuccess }: IQuoteCancelModal) => {
  const [selectedReason, setSelectedReason] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!selectedReason) return;
    const reasonLabel = cancellationReasons.find(r => r.value === selectedReason)?.label ?? selectedReason;
    try {
      setIsLoading(true);
      await QuoteService.cancelQuote(quoteId, reasonLabel);
      await onSuccess?.();
      handleClose();
    } catch {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  const cancellationReasons = [
    { id: 1, value: 'quote_duplicada', label: 'Quote duplicada' },
    { id: 2, value: 'sin_operacion', label: 'No tenemos operación en ese país' }
  ];

  const handleClose = () => {
    if (isLoading) return;
    onClose();
    setSelectedReason(undefined);
  };

  if (!open) return null;

  return (
    <BordModal
      closesOnClickOutside={false}
      modalContent={
        <div className="quoteCancelModal">
          <div className="modalCard">
            <div className="closeBtn" onClick={handleClose}>
              <BordOneToneIcon variant="x" standardSize={16} stroke="#6F737D" />
            </div>
            <div className="modalIcon">
              <BordOneToneIcon variant="alertTriangleWarning" customWidth="6.5rem" stroke="#DE072E" />
            </div>
            <div className="modalTitle">Cancelar cotización</div>
            <div className="modalSubtitle">
              Esta acción no se puede deshacer. El cliente recibirá un correo con
              <br /> la razón de cancelación.
            </div>
            <div className="modalReason">
              <BordSelect
                label="Razón de cancelación*"
                placeholder="Selecciona un motivo"
                options={cancellationReasons}
                value={selectedReason || ''}
                onChange={value => value && setSelectedReason(value)}
                className="customBordSelect"
                triggerWidth={480}
                dropdownWidth={480}
              />
            </div>
            <BordButton
              label="Confirmar cancelación"
              customClassName="confirmBtn"
              disabled={!selectedReason}
              isLoading={isLoading}
              onClick={handleConfirm}
            />
            <div className="textButtonContainer">
              <BordTextButton text="Volver y mantener cotización" onClick={handleClose} className="textButton" />
            </div>
          </div>
        </div>
      }
    />
  );
};

export default QuoteCancelModal;
